import {
  StateFilterBoolean,
  StateFilterDateRange,
  StateFilterRange,
  StateFilterString,
  ConfigJson,
  StateFilterValue
} from "./filters";
import { Transition, TransitionProps } from "./transition";

export interface StateStatsEvent {
  code: number;
  total: number;
  statusDescription: string;
  answered?: boolean;
}

export interface StateStats {
  finished: boolean;
  events: StateStatsEvent[];
}

export interface PipelineStatsPayload {
  id: number;
  pipelineFinished: boolean;
  states: {
    [stateId: string]: StateStats;
  };
}

export interface StateProps {
  id: number;
  operation:
    | "HIGIENIZACAO_OFFLINE"
    | "ANALISE_IA"
    | "REFIN"
    | "SAQUE_COMPLEMENTAR"
    | "FILTRO"
    | "DIGITACAO"
    | "TELEFONIA"
    | "SMS"
    | "WHATS"
    | "ENVIA_ATENDIMENTO"
    | "END"
    | "START";
  stateType: "NOOP" | "EXECUTOR";
  banco?: string;
  convenio?: string;
  codigoTabela?: string;
  idConvenio?: string;
  tipoCartao?: string;
  tabela?: string;
  usuarioDigitacao?: string;
  usuarioDigitacaoSenha?: string;
  cpfUsuarioCertificado?: string;
  tipoColunaMargem?: string;
  operacao?: string;

  paused?: boolean;
  nome?: string;
  name?: string;
  mensagem?: string;
  quantidadeMaximaTelLead?: number;
  mappedFields?: string[];
  transitions?: TransitionProps[];
  configJson?: Record<string, StateFilterValue>;

  qtdEstimadaEnvioDia?: number;
  enabledMensagemSaudacao?: boolean;
  type?: string;
  usedHeaders?: string;
  mensagemNormal?: string;
  mensagemSaudacao?: string;
  wppSessionIds?: number[];
  fileType?: "audio" | "video" | "image" | "document";
  filename?: string;
  fileSize?: number;
  analiseExecuted?: boolean;
}

export type StateFilter =
  | StateFilterRange
  | StateFilterDateRange
  | StateFilterBoolean
  | StateFilterString;

// export type ConfigJson = Map<string, StateFilter>;

export class State {
  id: StateProps["id"];
  operation: StateProps["operation"];
  stateType: StateProps["stateType"];
  banco?: StateProps["banco"];
  convenio: StateProps["convenio"];
  codigoTabela: StateProps["codigoTabela"];
  idConvenio?: StateProps["idConvenio"];
  tipoCartao?: StateProps["tipoCartao"];
  tabela?: StateProps["tabela"];
  usuarioDigitacao?: StateProps["usuarioDigitacao"];
  usuarioDigitacaoSenha?: StateProps["usuarioDigitacaoSenha"];
  cpfUsuarioCertificado?: StateProps["cpfUsuarioCertificado"];
  tipoColunaMargem?: StateProps["tipoColunaMargem"];
  operacao?: StateProps["operacao"];
  paused?: StateProps["paused"];
  nome?: StateProps["nome"];
  name?: StateProps["name"];
  mensagem?: StateProps["mensagem"];
  quantidadeMaximaTelLead?: StateProps["quantidadeMaximaTelLead"];
  mappedFields?: StateProps["mappedFields"];
  configJson?: ConfigJson;
  transitions?: Transition[];

  qtdEstimadaEnvioDia?: StateProps["qtdEstimadaEnvioDia"];
  enabledMensagemSaudacao?: StateProps["enabledMensagemSaudacao"];
  type?: StateProps["type"];
  usedHeaders?: StateProps["usedHeaders"];
  mensagemNormal?: StateProps["mensagemNormal"];
  mensagemSaudacao?: StateProps["mensagemSaudacao"];
  wppSessionIds?: StateProps["wppSessionIds"];
  fileType?: StateProps["fileType"];
  filename?: StateProps["filename"];
  fileSize?: StateProps["fileSize"];
  analiseExecuted?: StateProps["analiseExecuted"];

  constructor({
    id,
    operation,
    stateType,
    banco,
    convenio,
    codigoTabela,
    idConvenio,
    tipoCartao,
    tabela,
    usuarioDigitacao,
    usuarioDigitacaoSenha,
    cpfUsuarioCertificado,
    tipoColunaMargem,
    operacao,
    paused,
    nome,
    name,
    mensagem,
    quantidadeMaximaTelLead,
    mappedFields,
    transitions,
    configJson,
    qtdEstimadaEnvioDia,
    enabledMensagemSaudacao,
    type,
    usedHeaders,
    mensagemNormal,
    mensagemSaudacao,
    wppSessionIds,
    fileType,
    filename,
    fileSize,
    analiseExecuted
  }: StateProps) {
    this.id = id;
    this.operation = operation;
    this.stateType = stateType;
    this.banco = banco;
    this.convenio = convenio;
    this.codigoTabela = codigoTabela;
    this.idConvenio = idConvenio;
    this.tipoCartao = tipoCartao;
    this.tabela = tabela;
    this.usuarioDigitacao = usuarioDigitacao;
    this.usuarioDigitacaoSenha = usuarioDigitacaoSenha;
    this.cpfUsuarioCertificado = cpfUsuarioCertificado;
    this.tipoColunaMargem = tipoColunaMargem;
    this.operacao = operacao;
    this.paused = operation === "ANALISE_IA" ? (paused ?? true) : paused;
    this.nome = nome;
    this.name = name;
    this.mensagem = mensagem;
    this.quantidadeMaximaTelLead = quantidadeMaximaTelLead;
    this.mappedFields = mappedFields || [];
    this.transitions = transitions?.map(
      (transition) => new Transition(transition)
    );
    this.configJson = configJson ? new ConfigJson(configJson) : undefined;
    this.qtdEstimadaEnvioDia = qtdEstimadaEnvioDia;
    this.enabledMensagemSaudacao = enabledMensagemSaudacao;
    this.type = type;
    this.usedHeaders = usedHeaders;
    this.mensagemNormal = mensagemNormal;
    this.mensagemSaudacao = mensagemSaudacao;
    this.wppSessionIds = wppSessionIds;
    this.fileType = fileType;
    this.filename = filename;
    this.fileSize = fileSize;
    this.analiseExecuted = analiseExecuted;
  }

  public addTransition(transition: Transition) {
    this.transitions = [...(this.transitions || []), transition];
  }

  public formatToSend(): StateProps {
    const formatted: Partial<StateProps> = {};
    if (this.id) formatted["id"] = this.id;
    if (this.operation) formatted["operation"] = this.operation;
    if (this.stateType) formatted["stateType"] = this.stateType;
    if (this.banco) formatted["banco"] = this.banco;
    if (this.convenio) formatted["convenio"] = this.convenio;
    if (this.codigoTabela) formatted["codigoTabela"] = this.codigoTabela;
    if (this.idConvenio) formatted["idConvenio"] = this.idConvenio;
    if (this.tipoCartao) formatted["tipoCartao"] = this.tipoCartao;
    if (this.tabela) formatted["tabela"] = this.tabela;
    if (this.usuarioDigitacao)
      formatted["usuarioDigitacao"] = this.usuarioDigitacao;
    if (this.usuarioDigitacaoSenha)
      formatted["usuarioDigitacaoSenha"] = this.usuarioDigitacaoSenha;
    if (this.cpfUsuarioCertificado)
      formatted["cpfUsuarioCertificado"] = this.cpfUsuarioCertificado;
    if (this.tipoColunaMargem)
      formatted["tipoColunaMargem"] = this.tipoColunaMargem;
    if (this.operacao) formatted["operacao"] = this.operacao;
    if (this.paused !== undefined) formatted["paused"] = this.paused;
    if (this.nome) formatted["nome"] = this.nome;
    if (this.name) formatted["name"] = this.name;
    if (this.mensagem) formatted["mensagem"] = this.mensagem;
    if (this.quantidadeMaximaTelLead !== undefined)
      formatted["quantidadeMaximaTelLead"] = this.quantidadeMaximaTelLead;
    if (this.mappedFields?.length)
      formatted["mappedFields"] = this.mappedFields;
    if (this.transitions) {
      formatted["transitions"] = this.transitions.map((transition) =>
        transition.formatToSend()
      );
    }
    if (this.configJson) {
      formatted["configJson"] = this.configJson.formatToSend();
    }
    if (this.qtdEstimadaEnvioDia !== undefined)
      formatted["qtdEstimadaEnvioDia"] = this.qtdEstimadaEnvioDia;
    if (this.enabledMensagemSaudacao !== undefined)
      formatted["enabledMensagemSaudacao"] = this.enabledMensagemSaudacao;
    if (this.type) formatted["type"] = this.type;
    if (this.usedHeaders) formatted["usedHeaders"] = this.usedHeaders;
    if (this.mensagemNormal) formatted["mensagemNormal"] = this.mensagemNormal;
    if (this.mensagemSaudacao)
      formatted["mensagemSaudacao"] = this.mensagemSaudacao;
    if (this.wppSessionIds?.length)
      formatted["wppSessionIds"] = this.wppSessionIds;
    if (this.fileType) formatted["fileType"] = this.fileType;
    if (this.filename) formatted["filename"] = this.filename;
    if (this.fileSize !== undefined) formatted["fileSize"] = this.fileSize;
    if (this.analiseExecuted !== undefined)
      formatted["analiseExecuted"] = this.analiseExecuted;
    return formatted as StateProps;
  }
}
// {
//     "idCustomer": 1,
//     "idUser": 1,
//     "initState": 1,
//     "endState": 5,
//     "idMailing": 7,
//     "states": [
//         {
//             "id": 1,
//             "operation": "REFIN",
//             "stateType": "NOOP",
//             "transitions": [
//                 {
//                     "event": "START",
//                     "target": 2
//                 },
//                 {
//                     "event": "START",
//                     "target": 3
//                 }
//             ]
//         },
//         {
//             "id": 2,
//             "operation": "REFIN",
//             "stateType": "EXECUTOR",
//             "banco": "ITAU",
//             "convenio": "INSS",
//             "codigoTabela": "57916",
//             "transitions": [
//                 {
//                     "event": "SUCCESS",
//                     "target": 4
//                 },
//                 {
//                     "event": "ERROR",
//                     "target": 5
//                 }
//             ]
//         },
//         {
//             "id": 3,
//             "operation": "REFIN",
//             "banco": "FACTA",
//             "convenio": "INSS",
//             "stateType": "EXECUTOR",
//             "transitions": [
//                 {
//                     "event": "SUCCESS",
//                     "target": 4
//                 },
//                 {
//                     "event": "ERROR",
//                     "target": 5
//                 }
//             ]
//         },
//         {
//             "id": 4,
//             "operation": "FILTRO",
//             "stateType": "EXECUTOR",
//             "configJson": {
//                 "valor_parcela": {
//                     "gte": 500,
//                     "lte": 10000
//                 },
//                 "quantidadeContratos": {
//                     "gte": 2,
//                     "lte": 5
//                 }
//             },
//             "transitions": [
//                 {
//                     "event": "SUCCESS",
//                     "target": 5
//                 },
//                 {
//                     "event": "ERROR",
//                     "target": 5
//                 }
//             ]
//         },
//         {
//             "id": 5,
//             "stateType": "EXECUTOR",
//             "operation": "END"
//         }
//     ]
// }
